import isEmpty from 'lodash/isEmpty'
import findIndex from 'lodash/findIndex'
import React, { useCallback, useMemo, useRef } from 'react'
import type { User } from '@ztechapps/common.contract'
import type { DragSortEvent } from '@ztechapps/webui.util'
import { Table } from '@ztechapps/webui.data-tools'
import { ListRow } from './ListRow'
import { ListHeader } from './ListHeader'
import { TextInput } from '@ztechapps/webui.input'
import { BusyButton } from '@ztechapps/webui.button'
import { generateId, jsonTryParse } from '@ztechapps/common.utils'
import type { TemplateTask, TemplateStatusAction } from './TaskTemplateStatusActionEditor'
import { normalizeMembers, toPersistableTasks, withDragKeys } from './templateTaskHelpers'

// `value`/`onChange` are the template-framework value contract (a JSON string +
// its setter). Remaining props are injected by the framework and forwarded to the
// "add task" TextInput, so they take its prop shape (minus the two we own here).
type TaskTemplateInputComponentProps = {
  value?: string
  onChange: (value: string) => void
} & Omit<React.ComponentProps<typeof TextInput>, 'value' | 'onChange'>

export const TaskTemplateInputComponent = ({ value, onChange, ...props }: TaskTemplateInputComponentProps) => {
  const [valueObj, setValueObj] = React.useState<TemplateTask[]>([])
  const [newTaskValue, setNewTaskValue] = React.useState('')

  // Mirror of the latest list. Lets the row change-handlers stay referentially
  // stable (empty deps) while still reading current data — without a ref they
  // would close over a stale `valueObj`, and the table's row memoization would
  // keep that stale closure when only a sibling row's data changed.
  const valueObjRef = useRef<TemplateTask[]>(valueObj)
  valueObjRef.current = valueObj

  React.useEffect(() => {
    if (!value) return
    // The parent form round-trips our committed value back through this prop.
    // Re-parsing our own echo would regenerate every task's transient drag `_id`,
    // remounting all rows — which breaks the FLIP reorder animation and can disrupt
    // an in-flight drag. Only re-derive when `value` changed externally (i.e. differs
    // from the JSON our last commit produced).
    if (value === JSON.stringify(toPersistableTasks(valueObjRef.current))) return
    const parsedValue = jsonTryParse(value)
    if (Array.isArray(parsedValue)) setValueObj(withDragKeys(parsedValue))
  }, [value])

  // Commits the in-memory list to state and persists it as Template.value JSON,
  // stripping the transient drag-sort `_id` so only `{ name, members?,
  // statusActions? }` is stored.
  const commit = useCallback(
    (tasks: TemplateTask[]) => {
      setValueObj(tasks)
      onChange(JSON.stringify(toPersistableTasks(tasks)))
    },
    [onChange]
  )

  // Resolve a task's live index by its stable drag-key. The table memoizes rows
  // by `_id`, so a row's `_config.index` can go stale after a reorder — keying
  // mutations by `taskId` (not a captured index) always hits the right task.
  const indexOfTask = useCallback((taskId: string) => findIndex(valueObjRef.current, (task) => task._id === taskId), [])

  const handleOnDelete = useCallback(
    (taskId: string) => {
      const index = indexOfTask(taskId)
      if (index < 0) return
      const newValue = [...valueObjRef.current]
      newValue.splice(index, 1)
      commit(newValue)
    },
    [commit, indexOfTask]
  )

  const handleOnChange = useCallback(
    (name: string, taskId: string) => {
      const index = indexOfTask(taskId)
      if (index < 0) return
      const newValue = [...valueObjRef.current]
      newValue[index] = { ...newValue[index], name }
      commit(newValue)
    },
    [commit, indexOfTask]
  )

  const handleStatusActionsChange = useCallback(
    (statusActions: TemplateStatusAction[], taskId: string) => {
      const index = indexOfTask(taskId)
      if (index < 0) return
      const newValue = [...valueObjRef.current]
      newValue[index] = {
        ...newValue[index],
        statusActions: !isEmpty(statusActions) ? statusActions : undefined,
      }
      commit(newValue)
    },
    [commit, indexOfTask]
  )

  const handleMembersChange = useCallback(
    (members: User[], taskId: string) => {
      const index = indexOfTask(taskId)
      if (index < 0) return
      const newValue = [...valueObjRef.current]
      newValue[index] = {
        ...newValue[index],
        members: !isEmpty(members) ? normalizeMembers(members) : undefined,
      }
      commit(newValue)
    },
    [commit, indexOfTask]
  )

  const handleDragSort = useCallback(
    ({ oldIndex, newIndex }: DragSortEvent) => {
      const newValue = [...valueObjRef.current]
      const [moved] = newValue.splice(oldIndex, 1)
      newValue.splice(newIndex, 0, moved)
      commit(newValue)
    },
    [commit]
  )

  const handleAddTask = useCallback(() => {
    commit([...valueObjRef.current, { _id: generateId(), name: newTaskValue }])
    setNewTaskValue('')
  }, [commit, newTaskValue])

  // Stable row component for the drag-sort-aware `row=` path. Injects the change
  // handlers (shared across all rows, not row data) into each row. The handlers
  // target a task by its stable `_id`, so they tolerate the table reordering.
  const Row = useMemo(
    () => (rowProps) => (
      <ListRow
        {...rowProps}
        handleOnDelete={handleOnDelete}
        handleOnChange={handleOnChange}
        handleStatusActionsChange={handleStatusActionsChange}
        handleMembersChange={handleMembersChange}
      />
    ),
    [handleOnDelete, handleOnChange, handleStatusActionsChange, handleMembersChange]
  )

  return (
    <div className={'row'}>
      <Table dataArray={valueObj} row={Row} onDragSort={handleDragSort} head={<ListHeader />} />
      <div className={'d-flex justify-content-between'}>
        <TextInput
          {...props}
          value={newTaskValue}
          onChange={(e) => {
            setNewTaskValue(e?.target?.value)
          }}
        />
        <div className={'px-3'}>
          <BusyButton icon={'plus'} onClick={handleAddTask} />
        </div>
      </div>
    </div>
  )
}
